import Bottombar from "@/components/bottombar"
import Button from "@/components/button"
import Combobox from "@/components/ui/combobox"
import {
  Sheet as ShadSheet,
  SheetContent as ShadSheetContent,
  SheetHeader as ShadSheetHeader,
  SheetTitle as ShadSheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import { Schema } from "@/rmpm/lib/schema"
import {
  createRMPMProductSheet,
  PMProductSchema,
  RMProductSchema,
} from "@/services/rmpm"
import {
  handleApiError,
  createBatchResult,
  showBatchResultToast,
} from "@/services/error-handler"
import useStore from "@/store"
import { selectPmTruck, selectRmTruck, TruckType } from "@/store/rmpm"
import { difference } from "lodash"
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router"

export interface AddTruckProductsSheetProps {
  truckType: TruckType
  sheetId: string
  truckNumber: string
  open?: boolean
  pmType?: string
  onClose?: () => void
}

export default function AddTruckProductsSheet({
  open,
  sheetId,
  truckNumber,
  truckType,
  pmType,
  onClose,
}: AddTruckProductsSheetProps) {
  const navigate = useNavigate()
  const rmpmStore = useStore.use.rmpm()
  const [selected, setSelected] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const options = useMemo(() => {
    let products: (RMProductSchema | PMProductSchema)[] = []

    if (truckType === "rm") {
      products = rmpmStore.allProducts.rmProducts
    } else {
      products = rmpmStore.allProducts.pmProducts
    }

    return products
      .filter((product) => {
        if (!pmType) {
          return true
        }

        return pmType === (product as PMProductSchema).packagingType
      })
      .map((product) => ({
        id: product.id,
        label: product.name,
        value: product.id,
        meta: {
          pmType: (product as PMProductSchema).packagingType,
        },
      }))
  }, [rmpmStore.allProducts, truckType, pmType])
  const { defaultSelected, setDefaultSelected } = useDefaultSelected()

  function useDefaultSelected() {
    const [defaultSelected, setDefaultSelected] = useState<string[]>([])

    useEffect(() => {
      let truck: Schema | undefined
      if (truckType == "rm") {
        truck = selectRmTruck(sheetId)(rmpmStore)
      } else {
        truck = selectPmTruck(sheetId)(rmpmStore)
      }

      if (!truck) {
        return
      }

      const ids: string[] =
        truck.properties?.products.items?.map(
          (product) => (product.properties?.id.const || "") as string
        ) || []

      setDefaultSelected(ids)
    }, [sheetId, rmpmStore.trucks])

    return {
      defaultSelected,
      setDefaultSelected,
    }
  }

  function handleOpenChange(open: boolean) {
    if (!open) {
      close()
    }
  }

  function handleSelectProducts(value: string[]) {
    setSelected(value as string[])
  }

  async function handleAddProducts() {
    setIsLoading(true)

    try {
      const newProducts = difference(selected, defaultSelected)

      if (newProducts.length) {
        // Track batch operation for product creation
        const batchResult = createBatchResult<string>(newProducts.length)

        // Create product sheets on server using the sheetId prop
        const productSheetIds: Record<string, string> = {}

        if (sheetId) {
          for (const productId of newProducts) {
            // Find the product details from allProducts
            const productDetails = truckType === "rm"
              ? rmpmStore.allProducts.rmProducts.find(p => p.id === productId)
              : rmpmStore.allProducts.pmProducts.find(p => p.id === productId)

            if (productDetails) {
              try {
                const response = await createRMPMProductSheet(sheetId, {
                  productId: productDetails.id,
                  productName: productDetails.name,
                  market: truckType === 'pm' ? 'DOMESTIC' : undefined,  // Default market for PM products
                })
                productSheetIds[productId] = response.productSheetId
                batchResult.successful.push(productId)
                batchResult.successCount++
              } catch (err) {
                const errorResult = await handleApiError(
                  err,
                  {
                    operation: `add product ${productDetails.name} to truck ${truckNumber}`,
                    context: { truckNumber, productId, sheetId },
                    showToast: false, // Will show aggregate toast
                  },
                  navigate
                )
                batchResult.failed.push({
                  item: productId,
                  error: errorResult.userMessage,
                })
                batchResult.failureCount++
                // Continue with other products even if one fails
              }
            }
          }
        }

        // Only add successfully created products to truck
        const successfulProductIds = Object.keys(productSheetIds)
        if (successfulProductIds.length > 0) {
          if (truckType === "rm") {
            rmpmStore.addProductsToRmTruck(sheetId, successfulProductIds, productSheetIds)
          } else {
            rmpmStore.addProductsToPmTruck(sheetId, successfulProductIds, productSheetIds)
          }

          setDefaultSelected([...defaultSelected, ...successfulProductIds])
        }

        // Show aggregate result
        showBatchResultToast(batchResult, "product")

        // Close sheet only if at least one product succeeded
        if (batchResult.successCount > 0) {
          close()
        }
      } else {
        close()
      }
    } catch (err) {
      await handleApiError(
        err,
        {
          operation: `add products to truck ${truckNumber}`,
          context: { truckNumber, sheetId, productCount: selected.length },
        },
        navigate
      )
    } finally {
      setIsLoading(false)
    }
  }

  function close() {
    if (onClose) {
      onClose()
    }
  }

  return (
    <ShadSheet open={open} onOpenChange={handleOpenChange}>
      <ShadSheetContent
        side="bottom"
        className="shadow-drop-shadow mx-auto max-w-md rounded-t-lg"
      >
        <ShadSheetHeader>
          <ShadSheetTitle className="text-xl">
            <div>Add Products</div>
          </ShadSheetTitle>

          <div className="text-base font-normal">
            You are adding product in Truck No.
            <span className="font-semibold">{truckNumber}</span>
          </div>
        </ShadSheetHeader>

        <SheetDescription className="sr-only">
          Select products to add
        </SheetDescription>

        <div className="flex flex-col gap-5 px-4 pb-8">
          <Combobox
            className="mt-2"
            label="Select Products (Multi select)"
            options={options}
            defaultValue={defaultSelected}
            lockDefaultValue
            multiple
            onChange={handleSelectProducts}
          />
        </div>

        <Bottombar className="flex gap-4 bg-white">
          <Button variant="outline" onClick={close} disabled={isLoading}>
            Back
          </Button>
          <Button
            variant="default"
            className="flex-1"
            onClick={handleAddProducts}
            disabled={isLoading}
          >
            {isLoading ? "Adding..." : "Add Products"}
          </Button>
        </Bottombar>
      </ShadSheetContent>
    </ShadSheet>
  )
}
